import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { attractionsSlide } from '../../shared/animations';
import { AttractionsBannerRevealService } from '../../services/attractions-banner-reveal.service';
import { formatAttractionPrice } from '../attraction-utils';

/** The two home attractions banners. Both stay compiled and working; the constant below picks one. */
export type AttractionsBannerStyle = 'classic' | 'mosaic';

/**
 * Which banner the menu renders.
 *
 * The single switch between `AttractionsBannerComponent` (one photo + text) and this one (spliced
 * photo billboard). Flip it to compare the two on device; nothing else needs to change — the scroll
 * reveal, the dismissal, the puck and the cart stacking all key off `showAttractionsBanner` and are
 * indifferent to which one renders.
 *
 * A template cannot read a module-level const, so `MenuComponent` re-exposes this as a field.
 */
export const ACTIVE_ATTRACTIONS_BANNER_STYLE: AttractionsBannerStyle = 'mosaic';

/** Height of the whole banner — photos fill it. The main size knob, and what the cart stacks on. */
export const ATTRACTIONS_MOSAIC_HEIGHT = 150;

/**
 * First-reveal enlarge/crop constants (Android/iOS twin). The banner's very first appearance per
 * page-session, with Reduce Motion off, peaks at `SCALE`× the resting height, holds, then settles
 * back. `SETTLE_MS` is documented here but physically lives in `.attr-mosaic--settling` in the SCSS,
 * since a stylesheet cannot read a TS const — keep the two in sync.
 */
export const ATTRACTIONS_EXPANDED_SCALE = 1.8;   // peak height = 150 × 1.8 = 270px
export const ATTRACTIONS_HOLD_MS = 500;          // hold at the expanded height before settling
export const ATTRACTIONS_SETTLE_MS = 700;        // settle 270 → 150 duration (mirrored in SCSS)
export const ATTRACTIONS_FIRST_SLIDE_MS = 560;   // first slide-up, 2× the normal 280ms

/** Horizontal travel of each seam from top to bottom — the slant. */
const SPLICE_SKEW = 20;

/** Which way a seam leans: `/` (+1) or `\` (−1). */
type SeamLean = 1 | -1;

/**
 * The seam pattern, written the way it reads on screen:
 * `pic / pic / pic \ pic \ pic` — five photos, four seams, the lean flipping at the middle so the
 * centre slice is a keystone.
 */
const SPLICE_SEAMS: SeamLean[] = [1, 1, -1, -1];

/** One photo slice: an absolutely-positioned box plus the polygon that trims it to its boundaries. */
export interface MosaicSlice {
  url: string;
  left: string;
  width: string;
  clipPath: string;
}

/** One seam line, clipped from the same boundary as the two slices it sits between. */
export interface MosaicSeam {
  left: string;
  width: string;
  clipPath: string;
}

/**
 * Home attractions banner, billboard variant (Android `AttractionsMosaicBanner`).
 *
 * Photos of the *same* attraction fill the banner, spliced by slanted seams in the `SPLICE_SEAMS`
 * pattern, with the copy laid over them the way the attractions hero does it — left-aligned on a
 * scrim that fades upward, so the whole card is one obvious tap target.
 *
 * Chrome matches the classic banner where it counts — full bleed and the same slide-up entrance — so
 * the two are drop-in swappable in the menu's bottom slot. It differs in having rounded top corners,
 * since here the photography, not a white surface, meets the menu.
 *
 * **Geometry has exactly one source of truth — the boundary lines.** Across width `W` and height `h`
 * with skew `s` and `n` slices, boundary `k` leaning `d` (+1 for `/`, −1 for `\`) runs from
 * `(k·W/n + d·s/2, 0)` down to `(k·W/n − d·s/2, h)`. Boundaries `0` and `n` are the vertical outer
 * edges (`d = 0`), so the ends stay flush with the banner instead of trailing off into wedges.
 *
 * Each slice is laid out `s` wider than its share and offset back by `s/2` — a box that covers both
 * leans — so its two boundaries land at fixed local coordinates (`s/2` and `100% − s/2`) and the clip
 * path is *derived* from them rather than eyeballed. The seams are then clipped from those same
 * lines, so slices and seams cannot disagree.
 *
 * **Why `clip-path` and not the repo's inline-SVG `<pattern>` idiom.** It is the only route that
 * keeps `<app-attraction-image>`, and with it the fade-in, the `ImageBroken.svg` fallback and the
 * re-validate-on-URL-change behaviour that every other image in this feature gets. A raw `<image>`
 * inside an SVG `<pattern>` would have none of them, and would need a unique `<defs>` id per slice.
 */
@Component({
    selector: 'app-attractions-mosaic-banner',
    templateUrl: './attractions-mosaic-banner.component.html',
    styleUrls: ['./attractions-mosaic-banner.component.scss'],
    animations: [attractionsSlide],
    host: { class: 'attractions-scope' },
    standalone: false
})
export class AttractionsMosaicBannerComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() city = '';
  /** Photos of the highlighted attraction, cover first. Fewer than the pattern needs is fine. */
  @Input() imageUrls: string[] = [];
  /** Raw cheapest price from the shop payload; drives the best-price variant when present. */
  @Input() fromPrice: number | null = null;
  @Input() currency: string | null = null;
  @Input() count = 0;
  /**
   * The highlighted attraction's own title, or null.
   *
   * Non-null switches the banner to naming the tour it is already showing photos of, with
   * `moreCount` in the corner and no second line; null keeps "Explore {city}" and the price/subtitle
   * line, which is what every venue showed before a highlight could be titled.
   */
  @Input() highlightTitle: string | null = null;
  /** How many other attractions there are besides the highlight. Read only with `highlightTitle`. */
  @Input() moreCount = 0;

  @Output() bannerClick = new EventEmitter<void>();
  @Output() dismiss = new EventEmitter<void>();

  /** Live banner height, bound to `.attr-mosaic`; settles from expanded → resting on the first reveal. */
  revealHeight = ATTRACTIONS_MOSAIC_HEIGHT;
  /** Frame peak height at the start of the reveal (150 × 1.8 = 270); the frame settles from here to 150. */
  readonly expandedHeight = ATTRACTIONS_MOSAIC_HEIGHT * ATTRACTIONS_EXPANDED_SCALE;
  /**
   * Fixed photo-box height/offset. The box is tall enough (and hangs below the frame) that the resting
   * 150px frame lands on the photo's vertical MIDDLE while the expanded 270px frame still fills to the
   * top with no gap — and the box never resizes, so the photo never scales or moves.
   *   height = 2·expanded − resting = 390;  bottom = resting − expanded = −120 (box spans −120 → 270).
   */
  readonly photoHeight = ATTRACTIONS_MOSAIC_HEIGHT * (2 * ATTRACTIONS_EXPANDED_SCALE - 1); // 390
  readonly photoBottom = ATTRACTIONS_MOSAIC_HEIGHT * (1 - ATTRACTIONS_EXPANDED_SCALE);      // -120
  /** Enter-slide duration passed to `@attractionsSlide`. Must stay a unit string (e.g. '560ms'). */
  enterSlideMs = '280ms';
  /** Turns the CSS height transition on for the settle only, so the snap to the expanded height is instant. */
  settling = false;

  private settleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private reveal: AttractionsBannerRevealService) {}

  /**
   * The first time the banner becomes visible this page-session, with motion allowed, play the
   * enlarge/crop reveal: snap to the expanded height under the slide, hold, then settle back. Every
   * later appearance (re-drop after dismiss, etc.) slides in normally at the resting height.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const v = changes['visible'];
    if (!v) {
      return;
    }
    if (!this.visible) {
      // Hidden or dismissed mid-reveal: drop any pending settle so it can't fire on the next show.
      this.clearSettle();
      return;
    }
    if (v.previousValue) {
      // Already visible (some other input changed) — not a fresh false→true reveal.
      return;
    }

    // Consume the once-flag on the first reveal regardless of Reduce Motion, so disabling Reduce
    // Motion mid-session can never replay the enlarge on a later appearance; gate only the visual.
    const enlarge = this.reveal.shouldAnimateOnce() && !this.prefersReducedMotion();
    this.enterSlideMs = enlarge ? `${ATTRACTIONS_FIRST_SLIDE_MS}ms` : '280ms';
    this.settling = false;
    this.clearSettle();

    if (enlarge) {
      // Snap to the expanded height with no transition (the element is created at this height in the
      // same change-detection pass, so it never animates its initial value).
      this.revealHeight = this.expandedHeight;
      this.settleTimer = setTimeout(() => {
        // Turn the height transition on and settle back — both in one tick, so only 270 → 150 animates.
        this.settling = true;
        this.revealHeight = ATTRACTIONS_MOSAIC_HEIGHT;
      }, ATTRACTIONS_HOLD_MS);
    } else {
      this.revealHeight = ATTRACTIONS_MOSAIC_HEIGHT;
    }
  }

  ngOnDestroy(): void {
    this.clearSettle();
  }

  private clearSettle(): void {
    if (this.settleTimer !== null) {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
  }

  /** Read per call so a mid-session OS change is respected. Mirrors event-media's SSR-safe guard. */
  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get formattedPrice(): string | null {
    return formatAttractionPrice(this.fromPrice, this.currency);
  }

  /** Whether the banner names one tour rather than the city. Drives title size, clamp and chip. */
  get hasHighlight(): boolean {
    return !!this.highlightTitle && this.highlightTitle.trim().length > 0;
  }

  /** The chip is pointless with nothing behind the highlight, and must never read "and 0 more". */
  get showsMoreChip(): boolean {
    return this.hasHighlight && this.moreCount > 0;
  }

  /** Up to as many photos as the seam pattern needs, blanks removed. */
  get photos(): string[] {
    return (this.imageUrls ?? [])
      .filter(u => !!u && u.trim().length > 0)
      .slice(0, SPLICE_SEAMS.length + 1);
  }

  /**
   * One photo (or none) has no seams to draw, so the banner renders it plain and full-bleed.
   *
   * Deliberately not "repeat the one photo across five slices with different crops": identical
   * photography either side of a seam reads as a rendering bug, not a mosaic. Android takes the same
   * early return.
   */
  get isSinglePhoto(): boolean {
    return this.photos.length < 2;
  }

  get singlePhoto(): string | null {
    return this.photos[0] ?? null;
  }

  /**
   * With fewer photos than the pattern expects, keep the leading seams: the pattern still reads left
   * to right, it is simply cut short. So three photos give `pic / pic / pic` and never reach the
   * backward keystone.
   */
  private get seamLeans(): SeamLean[] {
    return SPLICE_SEAMS.slice(0, this.photos.length - 1);
  }

  get slices(): MosaicSlice[] {
    const photos = this.photos;
    const n = photos.length;
    const leans = this.seamLeans;
    const half = SPLICE_SKEW / 2;

    return photos.map((url, index) => {
      // Outer edges are vertical; inner ones follow the pattern. Slice `index` is bounded by
      // boundary `index` on the left and `index + 1` on the right, and boundary k (for 1..n-1) is
      // seam k - 1 — hence the index shift, and the 0 for the two ends.
      const leftLean = leans[index - 1] ?? 0;
      const rightLean = leans[index] ?? 0;

      const leftTop = half + leftLean * half;
      const leftBottom = half - leftLean * half;
      const rightTop = rightLean * half;
      const rightBottom = -rightLean * half;

      return {
        url,
        left: `calc(${index} * 100% / ${n} - ${half}px)`,
        width: `calc(100% / ${n} + ${SPLICE_SKEW}px)`,
        // Percentages inside clip-path resolve against the slice's own border box, which is exactly
        // its width — so the right-hand vertices are written relative to 100%, not as absolute px
        // that only the browser could know.
        clipPath: `polygon(${leftTop}px 0, calc(100% - ${half}px + ${rightTop}px) 0, ` +
          `calc(100% - ${half}px + ${rightBottom}px) 100%, ${leftBottom}px 100%)`
      };
    });
  }

  /**
   * The seam strokes, clipped from the same boundary lines as the slices.
   *
   * Drawn as an overlay ABOVE the slices rather than as gaps between them, for the reason Android
   * strokes a line over its clip paths: the paths meet exactly on the boundary, so an overlay is
   * what covers the sub-pixel hairline where they abut. Insetting the polygons instead would expose
   * the banner's own `--attr-surface-variant` rather than the specified white, and a border on each
   * slice would double the stroke at every join.
   *
   * Clipped rather than `skewX()`-ed on purpose: a skew angle depends on the banner's height, so it
   * would silently drift the moment safe-area padding changes it. This derives from the boundary.
   */
  get seams(): MosaicSeam[] {
    const n = this.photos.length;
    const half = SPLICE_SKEW / 2;
    const stroke = 3;

    return this.seamLeans.map((lean, seamIndex) => {
      // Boundary k = seamIndex + 1, centred at k·W/n, travelling ±half from top to bottom. The box
      // is the stroke plus the full travel either side, so the polygon has room for both leans.
      const boxHalf = half + stroke / 2;
      const topCentre = boxHalf + lean * half;
      const bottomCentre = boxHalf - lean * half;

      return {
        left: `calc(${seamIndex + 1} * 100% / ${n} - ${boxHalf}px)`,
        width: `${boxHalf * 2}px`,
        clipPath: `polygon(${topCentre - stroke / 2}px 0, ${topCentre + stroke / 2}px 0, ` +
          `${bottomCentre + stroke / 2}px 100%, ${bottomCentre - stroke / 2}px 100%)`
      };
    });
  }

  trackBySlice(index: number): number {
    return index;
  }

  onDismiss(event: Event): void {
    // A DOM click bubbles where Compose consumes it: without this the dismiss would also fire the
    // banner's own click and navigate to the list.
    event.stopPropagation();
    this.dismiss.emit();
  }
}
